import React, { useMemo } from "react";
import {
  Stack,
  Box,
  Switch,
  FormControlLabel,
  Typography,
  alpha,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import FormAlert from "../components/common/FormAlert";
import FormSelectField from "../components/common/FormSelectField";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { updateBranchSettings } from "../redux/actions/settingsActions";
import { BranchSettingsResponseDto } from "../shared/dtos/branchSettings/BranchSettingsResponseDto";
import { BranchSettingsRequestDto } from "../shared/dtos/branchSettings/BranchSettingsRequestDto";
import { settingsActions } from "../redux/slices/settings";

// --- Interfaces ---

interface IntegrationFormValues {
  active: boolean;
  gatewayApiKey: string;
  gatewayWebhookSecret: string;
  hasGatewayApiKey: boolean;
}

interface PaymentFormValues extends IntegrationFormValues {
  allowsMpesaStk: boolean;
  allowsMpesaC2b: boolean;
  allowsBankIpn: boolean;
}

interface CommunicationFormValues extends IntegrationFormValues {
  allowsSms: boolean;
  allowsEmail: boolean;
  allowsWhatsapp: boolean;
  allowsUssd: boolean;
}

interface BranchSettingsFormValues {
  etimsEnabled: boolean;
  accessToken: string;
  timezone: string;
  defaultCurrencyId: string;
  defaultPaymentMethodId: string | "";
  defaultCustomerId: string | "";
  receiptFooterMessage: string;
  paymentIntegration: PaymentFormValues;
  communicationIntegration: CommunicationFormValues;
}

// --- Constants ---

const TIMEZONES = [
  { value: "Africa/Nairobi", label: "Africa/Nairobi (EAT)" },
  { value: "Africa/Kampala", label: "Africa/Kampala (EAT)" },
  { value: "Africa/Dar_es_Salaam", label: "Africa/Dar_es_Salaam (EAT)" },
  { value: "Africa/Johannesburg", label: "Africa/Johannesburg (SAST)" },
  { value: "Africa/Lagos", label: "Africa/Lagos (WAT)" },
  { value: "Europe/London", label: "Europe/London (GMT/BST)" },
  { value: "UTC", label: "UTC" },
];

// --- Schema ---

const settingsSchema: Yup.ObjectSchema<BranchSettingsFormValues> = Yup.object({
  etimsEnabled: Yup.boolean().required(),
  accessToken: Yup.string().ensure(),
  timezone: Yup.string().required("Timezone is required"),
  defaultCurrencyId: Yup.string().required("Default currency is required"),
  defaultPaymentMethodId: Yup.string().ensure(),
  defaultCustomerId: Yup.string().ensure(),
  receiptFooterMessage: Yup.string().ensure(),
  paymentIntegration: Yup.object({
    active: Yup.boolean().required(),
    gatewayApiKey: Yup.string().ensure(),
    gatewayWebhookSecret: Yup.string().ensure(),
    hasGatewayApiKey: Yup.boolean().required(),
    allowsMpesaStk: Yup.boolean().required(),
    allowsMpesaC2b: Yup.boolean().required(),
    allowsBankIpn: Yup.boolean().required(),
  }).required(),
  communicationIntegration: Yup.object({
    active: Yup.boolean().required(),
    gatewayApiKey: Yup.string().ensure(),
    gatewayWebhookSecret: Yup.string().ensure(),
    hasGatewayApiKey: Yup.boolean().required(),
    allowsSms: Yup.boolean().required(),
    allowsEmail: Yup.boolean().required(),
    allowsWhatsapp: Yup.boolean().required(),
    allowsUssd: Yup.boolean().required(),
  }).required(),
});

const UpdateBranchSettingsForm: React.FC<{
  branchSettings: BranchSettingsResponseDto | null;
  loading: boolean;
  error: string | null;
}> = ({ branchSettings, loading = false, error = null }) => {
  const dispatch = useAppDispatch();
  const { items: currencies } = useAppSelector((state) => state.currency);
  const { items: paymentMethods } = useAppSelector(
    (state) => state.paymentMethod,
  );
  const { items: customers } = useAppSelector((state) => state.customer);

  const currencyOptions = useMemo(
    () => currencies?.map((c) => ({ value: c.id, label: c.code })) || [],
    [currencies],
  );

  const paymentOptions = useMemo(
    () => [
      { value: "", label: "-- None --" },
      ...(paymentMethods?.map((p) => ({ value: p.id, label: p.name })) || []),
    ],
    [paymentMethods],
  );

  const customerOptions = useMemo(
    () => [
      { value: "", label: "-- None --" },
      ...(customers?.map((c) => ({
        value: c.id,
        label: `${c.name} (${c.primaryContact})`,
      })) || []),
    ],
    [customers],
  );

  const initialValues: BranchSettingsFormValues = useMemo(
    () => ({
      etimsEnabled: branchSettings?.etimsEnabled || false,
      accessToken: "", // Secrets are never sent back from server
      timezone: branchSettings?.timezone || "Africa/Nairobi",
      defaultCurrencyId: branchSettings?.defaultCurrencyId || "",
      defaultPaymentMethodId: branchSettings?.defaultPaymentMethodId || "",
      defaultCustomerId: branchSettings?.defaultCustomerId || "",
      receiptFooterMessage: branchSettings?.receiptFooterMessage || "",
      paymentIntegration: {
        active: branchSettings?.paymentIntegration?.active || false,
        gatewayApiKey: "",
        gatewayWebhookSecret: "",
        allowsMpesaStk:
          branchSettings?.paymentIntegration?.allowsMpesaStk || false,
        allowsMpesaC2b:
          branchSettings?.paymentIntegration?.allowsMpesaC2b || false,
        allowsBankIpn:
          branchSettings?.paymentIntegration?.allowsBankIpn || false,
        hasGatewayApiKey:
          branchSettings?.paymentIntegration?.hasGatewayApiKey || false,
      },
      communicationIntegration: {
        active: branchSettings?.communicationIntegration?.active || false,
        gatewayApiKey: "",
        gatewayWebhookSecret: "",
        allowsSms: branchSettings?.communicationIntegration?.allowsSms || false,
        allowsEmail:
          branchSettings?.communicationIntegration?.allowsEmail || false,
        allowsWhatsapp:
          branchSettings?.communicationIntegration?.allowsWhatsapp || false,
        allowsUssd:
          branchSettings?.communicationIntegration?.allowsUssd || false,
        hasGatewayApiKey:
          branchSettings?.communicationIntegration?.hasGatewayApiKey || false,
      },
    }),
    [branchSettings],
  );

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(settingsActions.setErrorUpdateBranchSettings(null))
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={settingsSchema}
        onSubmit={(values) => {
          // Cast form values to Request DTO format
          const payload: BranchSettingsRequestDto = {
            ...values,
            // Convert empty strings from selects to null for the backend UUID fields
            defaultPaymentMethodId: values.defaultPaymentMethodId || undefined,
            defaultCustomerId: values.defaultCustomerId || undefined,
            accessToken: values.accessToken || undefined,
            paymentIntegration: {
              ...values.paymentIntegration,
              gatewayApiKey:
                values.paymentIntegration.gatewayApiKey || undefined,
              gatewayWebhookSecret:
                values.paymentIntegration.gatewayWebhookSecret || undefined,
            },
            communicationIntegration: {
              ...values.communicationIntegration,
              gatewayApiKey:
                values.communicationIntegration.gatewayApiKey || undefined,
              gatewayWebhookSecret:
                values.communicationIntegration.gatewayWebhookSecret ||
                undefined,
            },
          };
          dispatch(updateBranchSettings(payload));
        }}
      >
        {({
          values,
          setFieldValue,
          dirty,
          isValid,
        }: FormikProps<BranchSettingsFormValues>) => (
          <Form>
            <Stack spacing={4}>
              {/* General Settings */}
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormSelectField
                  name="defaultCurrencyId"
                  label="Default Currency"
                  options={currencyOptions}
                />
                <FormSelectField
                  name="timezone"
                  label="System Timezone"
                  options={TIMEZONES}
                />
                <FormSelectField
                  name="defaultPaymentMethodId"
                  label="Default Payment Mode"
                  options={paymentOptions}
                />
                <FormSelectField
                  name="defaultCustomerId"
                  label="Default Walk-in Customer"
                  options={customerOptions}
                />
              </Box>

              {/* Payment Gateway Section */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: (t) => alpha(t.palette.background.default, 0.4),
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    mb: 2,
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="overline" sx={{ fontWeight: 700 }}>
                    Payment Gateway
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {values.paymentIntegration.hasGatewayApiKey ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="caption">
                      {values.paymentIntegration.hasGatewayApiKey
                        ? "Configured"
                        : "Keys Missing"}
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="paymentIntegration.gatewayApiKey"
                    label="API Key"
                    type="password"
                    placeholder="••••••••"
                  />
                  <FormTextField
                    name="paymentIntegration.gatewayWebhookSecret"
                    label="Webhook Secret"
                    type="password"
                    placeholder="••••••••"
                  />
                </Box>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ mt: 2, flexWrap: "wrap" }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.paymentIntegration.active}
                        onChange={(e) =>
                          setFieldValue(
                            "paymentIntegration.active",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.paymentIntegration.allowsMpesaStk}
                        onChange={(e) =>
                          setFieldValue(
                            "paymentIntegration.allowsMpesaStk",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="M-Pesa STK"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.paymentIntegration.allowsMpesaC2b}
                        onChange={(e) =>
                          setFieldValue(
                            "paymentIntegration.allowsMpesaC2b",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="M-Pesa C2B"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.paymentIntegration.allowsBankIpn}
                        onChange={(e) =>
                          setFieldValue(
                            "paymentIntegration.allowsBankIpn",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Bank IPN"
                  />
                </Stack>
              </Box>

              {/* Communication Gateway Section */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    mb: 2,
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="overline" sx={{ fontWeight: 700 }}>
                    Communication Hub
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {values.communicationIntegration.hasGatewayApiKey ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="caption">
                      {values.communicationIntegration.hasGatewayApiKey
                        ? "Configured"
                        : "Keys Missing"}
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="communicationIntegration.gatewayApiKey"
                    label="API Key"
                    type="password"
                    placeholder="••••••••"
                  />
                  <FormTextField
                    name="communicationIntegration.gatewayWebhookSecret"
                    label="Webhook Secret"
                    type="password"
                    placeholder="••••••••"
                  />
                </Box>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ mt: 2, flexWrap: "wrap" }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.communicationIntegration.active}
                        onChange={(e) =>
                          setFieldValue(
                            "communicationIntegration.active",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.communicationIntegration.allowsSms}
                        onChange={(e) =>
                          setFieldValue(
                            "communicationIntegration.allowsSms",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="SMS"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.communicationIntegration.allowsWhatsapp}
                        onChange={(e) =>
                          setFieldValue(
                            "communicationIntegration.allowsWhatsapp",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="WhatsApp"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.communicationIntegration.allowsEmail}
                        onChange={(e) =>
                          setFieldValue(
                            "communicationIntegration.allowsEmail",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Email"
                  />
                </Stack>
              </Box>

              {/* Compliance Section */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: (t) => alpha(t.palette.info.main, 0.05),
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, mb: 1, display: "block" }}
                >
                  Tax Compliance (eTIMS)
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.etimsEnabled}
                      onChange={(e) =>
                        setFieldValue("etimsEnabled", e.target.checked)
                      }
                    />
                  }
                  label="Enable eTIMS Integration"
                />
                {values.etimsEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <FormTextField
                      name="accessToken"
                      label="eTIMS Access Token"
                      type="password"
                      placeholder="••••••••"
                    />
                  </Box>
                )}
              </Box>

              <FormTextField
                name="receiptFooterMessage"
                label="Receipt Footer Message"
                multiline
                rows={2}
                helperText="Appears at the bottom of all printed/digital receipts."
              />

              <SubmitButton
                title="Save Branch Settings"
                loading={loading}
                disabled={!dirty || !isValid}
                startIcon={<SaveOutlinedIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UpdateBranchSettingsForm;
